import type { StateCreator } from "zustand";
import { create } from "zustand";
import { type DevtoolsOptions, devtools } from "zustand/middleware";
import type { BarChartEventProps } from "@/components/ui/bar-chart";
import type {
  ArticleCategoryType,
  ArticleProminenceType,
  ArticleSentimentPolarityType,
  GetFeedArticlesPagedFromProjectionArticle,
} from "@/data-access/news-feeds/queries/articles-management-queries";
import type { FeedArticleProjectionOrdering } from "@/data-access/shared/types/feed-article-projection.types";
import { createOrdering } from "@/lib/utils/articleSortOrderingUtils";
import { createArticlesManagementActions } from "./feed-content-management.actions";

/**
 * Pagination state for articles management
 */
export interface PaginationState {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string | null;
  endCursor: string | null;
  totalCount: number | null;
}

/**
 * Current page structure for pagination
 * Uses projection article type directly
 */
export interface CurrentPage {
  edges: Array<{
    cursor: string;
    node: GetFeedArticlesPagedFromProjectionArticle;
  }>;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/**
 * Volume Graph Slice
 */
type VolumeGraphSlice = {
  feedVolume: number | null;
  setFeedVolume: (volume: number | null) => void;
  stackFeedVolumeBy: null | string;
  setStackFeedVolumeBy: (stackBy: null | string) => void;
  activeBar:
    | Omit<BarChartEventProps, "eventType" | "categoryClicked">
    | undefined;
  setActiveBar: (
    bar: Omit<BarChartEventProps, "eventType" | "categoryClicked"> | undefined,
  ) => void;
  chartSelection: string | null;
  setChartSelection: (selection: string | null) => void;
  resetFeedVolumeView: () => void;
};

/**
 * Article Management Slice (selection, modals, delete)
 */
type ArticleManagementSlice = {
  selectedArticleIds: Set<string>;
  excludedArticleIds: Set<string>;
  singleArticleIdForAction: string | null;
  isAllSelected: boolean;
  isDeleteModalOpen: boolean;
  isEditModalOpen: boolean;
  isRemoveArticleFromClusterModalOpen: boolean;
  isMoveArticleFromNarrativeClusterModalOpen: boolean;
  isNarrativeIntelligenceV2ModalOpen: boolean;
  insightsNarrativeClusterId: string;

  toggleArticleSelection: (id: string) => void;
  setSelectedArticles: (ids: Set<string>) => void;
  clearSelection: () => void;
  setIsAllSelected: (isAllSelected: boolean) => void;
  setSingleArticleIdForAction: (id: string | null) => void;
  setDeleteModalOpen: (open: boolean) => void;
  setEditModalOpen: (open: boolean) => void;
  setRemoveArticleFromClusterModalOpen: (open: boolean) => void;
  setMoveArticleFromNarrativeClusterModalOpen: (open: boolean) => void;
  setNarrativeIntelligenceV2ModalOpen: (open: boolean) => void;
  setInsightsNarrativeClusterId: (id: string) => void;
  resetModalState: () => void;
  deleteArticlesFromStore: (ids: string[]) => void;
  resetSelectionAfterBulkAction: (isSingleAction: boolean) => void;
};

/**
 * Article Edit State
 */
export type ArticleEditState = {
  author: string | null;
  category: ArticleCategoryType | null;
  datePublished: string | null;
  headline: string | null;
  isLede: boolean | null;
  prominence: ArticleProminenceType | null;
  publisher: string | null;
  sentimentPolarity: ArticleSentimentPolarityType | null;
  sentimentRationale: string | null;
  originalAiSentiment: {
    polarity: ArticleSentimentPolarityType;
    rationale: string;
  } | null; // track the original AI sentiment value
  summary: string | null;
  articleTagIds: string[];
};

/**
 * Article Edit Slice
 */
export type ArticleEditSlice = {
  editArticle: ArticleEditState;
  isUpdatingArticle: boolean;
  isMovingArticleFromNarrativeCluster: boolean;
  isRemovingArticleFromNarrativeCluster: boolean;

  setEditField: <K extends keyof ArticleEditState>(
    field: K,
    value: ArticleEditState[K],
  ) => void;
  setEditFields: (fields: Partial<ArticleEditState>) => void;
  clearEditFields: () => void;
  resetEditModal: () => void;
  populateEditFieldsForSingleArticle: (articleId: string) => void;
  updateArticlesInStore: (
    updatedArticles: GetFeedArticlesPagedFromProjectionArticle[],
  ) => void;
};

/**
 * Action Bar Slice
 */
type ActionBarSlice = {
  displayArticleActionBar: boolean;
  setDisplayArticleActionBar: (display: boolean) => void;
};

/** */
type FeedSearchSlice = {
  searchTerm: string;
  isAdvancedSearch: boolean;
  setSearchTerm: (searchTerm: string) => void;
  setIsAdvancedSearch: (isAdvancedSearch: boolean) => void;
};

/**
 * Sort Order Slice
 * Stores ordering in FeedArticleProjectionOrdering format (e.g., [{datePublished: "DESC_NULLS_LAST"}])
 */
type SortOrderSlice = {
  ordering: FeedArticleProjectionOrdering;
  setOrdering: (ordering: FeedArticleProjectionOrdering) => void;
};

/**
 * Feed content management slice (combines all slices)
 */
export type FeedContentManagementSlice = {
  // Fast lookups/updates
  articlesMap: Map<string, GetFeedArticlesPagedFromProjectionArticle>;

  // Current page for rendering
  currentPage: CurrentPage;

  // Loading and error states
  isInitialLoading: boolean;
  isFetchingArticles: boolean;
  error: string | null;

  // Pagination metadata
  pagination: PaginationState;

  // Search functionality
  searchTerm: string;
  isAdvancedSearch: boolean;

  // Data actions
  setInitialLoading: (loading: boolean) => void;
  setFetchingArticles: (fetching: boolean) => void;
  setError: (error: string | null) => void;
  setCurrentPage: (
    edges: Array<{
      cursor: string;
      node: GetFeedArticlesPagedFromProjectionArticle;
    }>,
    pageInfo: PaginationState,
  ) => void;
  appendArticles: (
    edges: Array<{
      cursor: string;
      node: GetFeedArticlesPagedFromProjectionArticle;
    }>,
    pageInfo: PaginationState,
  ) => void;
  resetState: () => void;
} & ArticleManagementSlice &
  ArticleEditSlice &
  VolumeGraphSlice &
  ActionBarSlice &
  FeedSearchSlice &
  SortOrderSlice;

/**
 * Initial pagination state
 */
const initialPaginationState: PaginationState = {
  hasNextPage: false,
  hasPreviousPage: false,
  startCursor: null,
  endCursor: null,
  totalCount: null,
};

/**
 * Initial current page state
 */
const initialCurrentPage: CurrentPage = {
  edges: [],
  hasNextPage: false,
  hasPreviousPage: false,
};

/**
 * Initial volume graph state
 */
const initialVolumeGraphState = {
  feedVolume: null,
  stackFeedVolumeBy: "impact_score",
  activeBar: undefined,
  chartSelection: null,
};

/**
 * Initial article management state (selection, modals)
 */
const initialArticleManagementState = {
  selectedArticleIds: new Set<string>(),
  excludedArticleIds: new Set<string>(),
  singleArticleIdForAction: null,
  isAllSelected: false,
  isDeleteModalOpen: false,
  isEditModalOpen: false,
  isRemoveArticleFromClusterModalOpen: false,
  isMoveArticleFromNarrativeClusterModalOpen: false,
  isNarrativeIntelligenceV2ModalOpen: false,
  insightsNarrativeClusterId: "",
};

/**
 * Initial article edit state
 */
const initialArticleEditState = {
  author: null,
  category: null,
  datePublished: null,
  headline: null,
  isLede: null,
  prominence: null,
  publisher: null,
  sentimentPolarity: null,
  sentimentRationale: null,
  originalAiSentiment: null,
  summary: null,
  articleTagIds: [],
};

/**
 * Initial action bar state
 */
const initialActionBarState = {
  displayArticleActionBar: false,
};

/**
 * Initial state
 */
const initialState = {
  articlesMap: new Map<string, GetFeedArticlesPagedFromProjectionArticle>(),
  currentPage: initialCurrentPage,
  isInitialLoading: false,
  isFetchingArticles: false,
  error: null,
  pagination: initialPaginationState,
  searchTerm: "",
  isAdvancedSearch: false,
  ordering: createOrdering("datePublished", "desc"),
  ...initialArticleManagementState,
  editArticle: initialArticleEditState,
  isUpdatingArticle: false,
  isMovingArticleFromNarrativeCluster: false,
  isRemovingArticleFromNarrativeCluster: false,
  ...initialVolumeGraphState,
  ...initialActionBarState,
};

const createFeedContentManagement: StateCreator<
  FeedContentManagementSlice,
  [["zustand/devtools", never]]
> = (set, get, api) => ({
  ...initialState,
  ...createArticlesManagementActions(set, get, api),
});

const useFeedContentManagementStore = create<FeedContentManagementSlice>()(
  devtools(createFeedContentManagement, {
    name: "feed-content-management-store",
    serialize: {
      options: true,
    },
  } as unknown as DevtoolsOptions),
);

export default useFeedContentManagementStore;
